import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import type { PluginId, Serialised } from '@squaredup/ids';
import { ConfirmationPrompt } from 'components/ConfirmationPrompt';
import LoadingSpinner from 'components/LoadingSpinner';
import type { ProjectedPlugin } from 'dynamo-wrapper';
import { ControlledApplicationTable, useApplicationTable } from 'pages/components/ApplicationTable/ApplicationTable';
import { useAllPlugins } from 'queries/hooks/useAllPlugins';
import { useReducer, type FC } from 'react';
import type { SetRequired } from 'type-fest';
import { SettingsTemplate } from '../SettingsTemplate';
import { useDeleteDerivedPluginMutation } from './useDeletePluginMutation';

type DerivedPlugin = {
    derivedPlugin: SetRequired<Serialised<ProjectedPlugin>, 'basePlugin'>;
    basePlugin: Serialised<ProjectedPlugin>;
};

interface DerivedPluginsTableProps {
    derivedPlugins: DerivedPlugin[];
}

export type DerivedPluginsTableState =
    | { deletionState: 'idle' }
    | { deletionState: 'confirming'; pluginIdToDelete: PluginId['value'] }
    | { deletionState: 'deleting'; pluginIdToDelete: PluginId['value'] };

type DerivedPluginsTableAction =
    | { type: 'startDelete'; pluginId: PluginId['value'] }
    | { type: 'confirmDelete' }
    | { type: 'cancelDelete' }
    | { type: 'finishDelete' };

const derivedPluginsTableReducer = (
    state: DerivedPluginsTableState,
    action: DerivedPluginsTableAction
): DerivedPluginsTableState => {
    switch (action.type) {
        case 'startDelete':
            return {
                deletionState: 'confirming',
                pluginIdToDelete: action.pluginId
            };

        case 'confirmDelete':
            if (state.deletionState !== 'confirming') {
                throw new Error('Cannot confirm delete when not in confirming state.');
            }

            return { ...state, deletionState: 'deleting' };

        case 'cancelDelete':
            return { ...state, deletionState: 'idle' };

        case 'finishDelete':
            if (state.deletionState !== 'deleting') {
                return state;
            }

            return { deletionState: 'idle' };

        default:
            return state;
    }
};

const DerivedPluginsTable: FC<DerivedPluginsTableProps> = ({ derivedPlugins }) => {
    const { deleteDerivedPluginAsync } = useDeleteDerivedPluginMutation();

    const [state, dispatch] = useReducer(derivedPluginsTableReducer, {
        deletionState: 'idle'
    });

    const table = useApplicationTable({
        columns: [
            {
                accessorKey: 'derivedPlugin.displayName',
                header: 'Name',
                maxSize: 20
            },
            {
                accessorKey: 'basePlugin.displayName',
                header: 'Base Data Source',
                maxSize: 20
            },
            {
                accessorKey: 'derivedPlugin.lastUpdated',
                cell: ({ row }) => {
                    if (row.original.derivedPlugin.lastUpdated == null) {
                        return '';
                    }

                    const date = new Date(row.original.derivedPlugin.lastUpdated);
                    return (
                        <span>
                            {date.toLocaleDateString()} {date.toLocaleTimeString()}
                        </span>
                    );
                },
                header: 'Last Updated',
                maxSize: 15
            },
            {
                accessorKey: 'derivedPlugin.author',
                header: 'Author',
                maxSize: 20
            },
            {
                accessorKey: 'derivedPlugin.id',
                cell: ({ row }) => <pre>{row.original.derivedPlugin.id}</pre>,
                header: 'ID',
                maxSize: 20
            }
        ],
        data: derivedPlugins,
        config: {
            actions: [
                {
                    icon: 'trash',
                    loading: (plugin) =>
                        state.deletionState === 'deleting' && state.pluginIdToDelete === plugin.derivedPlugin.id,
                    confirmationIcon: faCheck,
                    action: (plugin) => dispatch({ type: 'startDelete', pluginId: plugin.derivedPlugin.id })
                }
            ]
        },
        layoutVersion: 'v2'
    });

    return (
        <>
            <ControlledApplicationTable {...table} />
            {state.deletionState === 'confirming' && (
                <ConfirmationPrompt
                    title={`Delete ${derivedPlugins.find((p) => p.derivedPlugin.id === state.pluginIdToDelete)
                        ?.derivedPlugin.displayName}`}
                    prompt='Are you sure you want to delete this saved data source? This action cannot be undone.'
                    onConfirm={async () => {
                        dispatch({ type: 'confirmDelete' });

                        await deleteDerivedPluginAsync({ pluginId: state.pluginIdToDelete });

                        dispatch({ type: 'finishDelete' });
                    }}
                    onClose={() => dispatch({ type: 'cancelDelete' })}
                />
            )}
        </>
    );
};

export const DerivedPluginsManagement: FC = () => {
    const derivedPlugins = useAllPlugins({
        select: (data): DerivedPlugin[] =>
            data
                .filter(
                    (plugin): plugin is SetRequired<Serialised<ProjectedPlugin>, 'basePlugin'> =>
                        plugin.basePlugin != null
                )
                .map(
                    (derivedPlugin): DerivedPlugin => ({
                        derivedPlugin,
                        basePlugin: data.find((plugin) => plugin.id === derivedPlugin.basePlugin?.pluginId)!
                    })
                )
                .toSorted((a, b) => {
                    if (a.derivedPlugin.displayName == null || b.derivedPlugin.displayName == null) {
                        return 0;
                    }

                    return a.derivedPlugin.displayName.localeCompare(b.derivedPlugin.displayName, undefined, {
                        sensitivity: 'base'
                    });
                })
    });

    const content = derivedPlugins.isLoading ? (
        <div className='flex justify-center'>
            <LoadingSpinner />
        </div>
    ) : (
        <DerivedPluginsTable derivedPlugins={derivedPlugins.data ?? []} />
    );

    return (
        <SettingsTemplate
            title='Saved Data Sources'
            description='Data sources saved by users that use other data sources to get their data.'
            // learnMoreLink='https://squaredup.com/cloud/plugins'
            flex
        >
            {content}
        </SettingsTemplate>
    );
};
